import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  CheckCircle,
  TrendingUp,
  Target,
  Lightbulb,
  Eye,
  LogOut,
  Bell,
  AlertCircle,
  RefreshCw,
  Info,
  User,
  X,
  FileText,
  Clock,
} from "lucide-react";
import IdeasPageHeader from "./IdeasPageHeader";
import IdeaSelector from "./IdeaSelector";



// Score Bar Component (unchanged)
const ScoreBar = ({ score, label, color }) => {
  const numericScore = parseFloat(score) || 0;
  const percentage = Math.min((numericScore / 10) * 100, 100);

  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-gray-900 mb-1">
        {numericScore.toFixed(1)}
      </div>
      <div className="text-sm text-gray-600 mb-3">{label}</div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
        <div
          className="h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        ></div>
      </div>
      <div className="text-xsr text-gray-500">{percentage.toFixed(0)}%</div>
    </div>
  );
};

// Goal Alignment Item Component (unchanged)
const GoalAlignmentItem = ({ icon, label, percentage, color }) => {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
      <div className="flex items-center space-x-3">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            percentage > 40
              ? "bg-green-100"
              : percentage > 20
              ? "bg-blue-100"
              : "bg-gray-100"
          }`}
        >
          {icon}
        </div>
        <span className="text-xsr font-medium text-gray-900">{label}</span>
      </div>
      <div className="flex items-center space-x-2">
        <span className="text-lg font-bold" style={{ color }}>
          {percentage}%
        </span>
      </div>
    </div>
  );
};

// Tag Component (unchanged)
const Tag = ({ children, variant = "default" }) => {
  const baseClasses =
    "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap";
  const variantClasses = {
    default: "bg-blue-100 text-blue-800",
    success: "bg-green-100 text-green-800",
  };

  return (
    <span className={`${baseClasses} ${variantClasses[variant]}`}>
      {children}
    </span>
  );
};

// Category Match Item Component (unchanged)
const CategoryMatchItem = ({ label, percentage, color }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-xsr font-medium text-gray-900">{label}</span>
      <span className="text-xsr font-bold" style={{ color }}>
        {percentage}% match
      </span>
    </div>
  );
};

// Modal Component (fixed)
const IdeaDetailsModal = ({ ideaDetails, onClose, isLoading }) => {
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg font-medium">Loading idea details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ideaDetails) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto relative">
          {/* X button at top-right */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-800 hover:text-gray-600 transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="p-8 pt-14">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-black mb-4">Error</h2>
              <p className="text-lg text-black font-bold">
                No idea details available.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to format the core capabilities and impacted stakeholders
  const formatListItems = (items) => {
    if (!items) return "N/A";
    return items
      .split(",")
      .map((item) => item.trim())
      .join(", ");
  };

  // Helper function to handle attachments
  const renderAttachments = (attachments) => {
    if (!attachments)
      return <span className="text-gray-500 italic">No attachments</span>;

    return attachments.split(",").map((file, index) => {
      const filename = file.trim();
      return (
        <a
          key={index}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mr-3 mb-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors duration-200"
        >
          📎 {filename}
        </a>
      );
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
        {/* X button at top-right */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-blue-200 transition-colors z-10"
        >
          <X className="w-7 h-7" />
        </button>

        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 rounded-t-xl">
          <div className="flex justify-center items-center">
            <h2 className="text-2xl font-bold text-black">Idea Details</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Basic Information Section */}
          <div className="mb-8">
            <h3 className="section-title">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="info-card">
                <h4 className="field-label">Idea ID</h4>
                <p className="field-value">{ideaDetails.Id || "N/A"}</p>
              </div>
              <div className="info-card">
                <h4 className="field-label">Submitted On</h4>
                <p className="field-value">
                  {ideaDetails.Date} at {ideaDetails.Time || "N/A"}
                </p>
              </div>
              <div className="info-card">
                <h4 className="field-label">Title & Sponsor</h4>
                <p className="field-value">
                  {ideaDetails["Title & Sponsor"] || "N/A"}
                </p>
              </div>
              <div className="info-card">
                <h4 className="field-label">Submitted By</h4>
                <p className="field-value">
                  {ideaDetails["Submitted By"] || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Purpose and Outcome Section */}
          <div className="mb-8">
            <h3 className="section-title">Purpose & Outcome</h3>
            <div className="space-y-6">
              <div className="info-card">
                <h4 className="field-label">Purpose</h4>
                <p className="field-value whitespace-pre-line">
                  {ideaDetails.Purpose || "N/A"}
                </p>
              </div>
              <div className="info-card">
                <h4 className="field-label">Desired Outcome</h4>
                <p className="field-value whitespace-pre-line">
                  {ideaDetails["Desired Outcome"] || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Current and Future State Section */}
          <div className="mb-8">
            <h3 className="section-title">State Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="info-card">
                <h4 className="field-label">Current State</h4>
                <p className="field-value whitespace-pre-line">
                  {ideaDetails["Current State"] || "N/A"}
                </p>
              </div>
              <div className="info-card">
                <h4 className="field-label">Future State Vision</h4>
                <p className="field-value whitespace-pre-line">
                  {ideaDetails["Future State Vision"] || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Solution Section */}
          <div className="mb-8">
            <h3 className="section-title">Proposed Solution</h3>
            <div className="info-card">
              <h4 className="field-label">Business Solution</h4>
              <p className="field-value whitespace-pre-line">
                {ideaDetails["Proposed Business Solution"] || "N/A"}
              </p>
            </div>
          </div>

          {/* Requirements and Capabilities Section */}
          <div className="mb-8">
            <h3 className="section-title">Requirements & Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="info-card">
                <h4 className="field-label">Core Capabilities Required</h4>
                <p className="field-value">
                  {formatListItems(ideaDetails["Core Capabilities Required"]) ||
                    "N/A"}
                </p>
              </div>
              <div className="info-card">
                <h4 className="field-label">Constraints/Requirements</h4>
                <p className="field-value whitespace-pre-line">
                  {ideaDetails["Constraints/Requirements"] || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-8">
            <h3 className="section-title">Expected Benefits</h3>
            <div className="info-card">
              <p className="field-value whitespace-pre-line">
                {ideaDetails["Expected Benefits"] || "N/A"}
              </p>
            </div>
          </div>

          {/* Stakeholders and Risks Section */}
          <div className="mb-8">
            <h3 className="section-title">Stakeholders & Risks</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="info-card">
                <h4 className="field-label">Impacted Stakeholders</h4>
                <p className="field-value">
                  {formatListItems(ideaDetails["Impacted Stakeholders"]) ||
                    "N/A"}
                </p>
              </div>
              <div className="info-card">
                <h4 className="field-label">Known Risks or Concerns</h4>
                <p className="field-value whitespace-pre-line">
                  {ideaDetails["Known Risks or Concerns"] || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* IT Actions Section */}
          <div className="mb-8">
            <h3 className="section-title">IT Actions</h3>
            <div className="info-card">
              <h4 className="field-label">Requested IT Actions</h4>
              <p className="field-value whitespace-pre-line">
                {ideaDetails["Requested IT Actions"] || "N/A"}
              </p>
            </div>
          </div>

          {/* Attachments Section */}
          <div className="mb-8">
            <h3 className="section-title">Attachments</h3>
            <div className="info-card">
              <div className="flex flex-wrap">
                {renderAttachments(ideaDetails.Attachments)}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-8 py-6 border-t border-gray-200 rounded-b-xl">
          <div className="flex justify-end justify-content-center">
            <button
              onClick={onClose}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-black font-bold px-8 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// API service (unchanged)
const sentinelApiService = {
  async getSentinelData(ideaId) {
    try {
      
      const response = await fetch(`/api/sentinal/get_sentinal?Id=${ideaId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Sentinel API Error:", error);
      throw error;
    }
  },
  async getIdeaDetails(ideaId) {
    try {
      const response = await fetch(`/api/sentinal/get_idea?Id=${ideaId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Return the first item from the array (or null if empty)
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error("Get Idea Details API Error:", error);
      throw error;
    }
  },
};

// Main IdeaSentinel Component (updated layout)
const IdeaSentinel = ({ setActiveTab, selectedId, setSelectedIdeaId }) => {
  const [sentinelData, setSentinelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ideaDetails, setIdeaDetails] = useState(null);
  const [usingMockData, setUsingMockData] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Mock data (unchanged)
  const mockSentinelData = {
    alignment_score: 8.2,
    urgency_score: 6.5,
    value_score: 7.8,
    ai_summary:
      "This idea demonstrates strong strategic alignment with our organizational goals, particularly in customer experience enhancement and operational efficiency improvements. The proposed solution addresses current pain points while offering scalable benefits for future growth.",
    goal_alignment: {
      "Customer Experience": 45.2,
      "Operational Efficiency": 38.7,
      "Revenue Growth": 22.1,
      Innovation: 35.4,
      "Cost Reduction": 28.9,
    },
    auto_tags: [
      "customer-support",
      "ai",
      "automation",
      "efficiency",
      "digital-transformation",
    ],
    suggested_categories: [
      { category: "Customer Support", match_percentage: 85 },
      { category: "Operations", match_percentage: 72 },
      { category: "Technology", match_percentage: 65 },
      { category: "Process Improvement", match_percentage: 58 },
    ],
  };

  const mockIdeaDetails = {
    title: "Implement AI-powered customer support chatbot",
    description: `We should implement an AI chatbot to handle common customer inquiries and provide 24/7 support. This would improve response times, reduce workload on human agents, and enhance customer satisfaction.

Key Benefits:
- 24/7 availability for customer support
- Reduced response times for common queries
- Cost savings on human resources
- Improved customer satisfaction scores
- Scalable solution for growing customer base

Implementation would involve integrating with existing CRM systems and training the AI on our knowledge base.`,
    submitted_by: "John Doe",
    date: "2024-01-15",
    status: "Under Review",
    additionalDetails: {
      estimatedCost: "$50,000 - $75,000",
      timeline: "6-9 months",
      expectedROI: "200% in 2 years",
      departmentsInvolved: ["Customer Support", "IT", "Operations"],
      risks: [
        "Integration challenges",
        "User adoption",
        "AI training accuracy",
      ],
    },
  };

  const mockRecentActivities = [
    {
      title: "Idea evaluation completed",
      author: "AI Analysis System",
      timestamp: "2 hours ago",
    },
    {
      title: "Initial review by operations team",
      author: "Sarah Johnson",
      timestamp: "1 day ago",
    },
    {
      title: "Idea submitted for consideration",
      author: "John Doe",
      timestamp: "3 days ago",
    },
  ];


const ActivitiesModal = ({ activities, onClose }) => {
  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div 
      className="activities-modal-overlay"
      onClick={handleOutsideClick}
    >
      <div className="activities-modal-container">
        {/* Header with close button */}
        <div className="activities-modal-header">
          <div className="activities-modal-header-content">
            <h2 className="activities-modal-title">Activity History</h2>
            <button
              onClick={onClose}
              className="activities-modal-close-btn"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="activities-modal-content">
          {activities.map((activity, index) => (
            <div key={index} className="activity-item">
              <div className="activity-icon">
                {activity.Role && typeof activity.Role === 'string' && activity.Role.includes('AI') ? (
                  <Lightbulb className="w-5 h-5 text-blue-600" />
                ) : (
                  <User className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div className="activity-details">
                <div className="activity-event">{activity.Events}</div>
                <div className="activity-meta">
                  <span className="activity-role">{activity.Role || 'N/A'}</span>
                  <span className="activity-date">{activity.Date}</span>
                </div>
                {activity.Comments && (
                  <div className="activity-comments">{activity.Comments}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="activities-modal-footer">
          <button
            onClick={onClose}
            className="activities-modal-close-button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
  // Transform API response (unchanged)
  const transformApiResponse = (data) => {
    let transformedCategories = [];
    if (Array.isArray(data["Suggested Categories"])) {
      transformedCategories = data["Suggested Categories"]
        .map((categoryArray) => {
          if (Array.isArray(categoryArray) && categoryArray.length >= 2) {
            return {
              category: categoryArray[0],
              match_percentage: parseFloat(categoryArray[1]) || 0,
            };
          }
          return null;
        })
        .filter(Boolean);
    }

    return {
      alignment_score: parseFloat(data["Alignment Score"]) || 0,
      urgency_score: parseFloat(data["Urgency Score"]) || 0,
      value_score: parseFloat(data["Value Score"]) || 0,
      ai_summary: data["AI Summary"] || "No summary available",
      goal_alignment: data["Goal Alignment"] || {},
      auto_tags: Array.isArray(data["Auto Tags"]) ? data["Auto Tags"] : [],
      suggested_categories: transformedCategories,
      // Include additional idea details from the API
      idea_title: data["Idea Title"] || data["Title"] || "No title available",
      idea_description:
        data["Idea Description"] ||
        data["Description"] ||
        "No description available",
      submitted_by: data["Submitted By"] || "Unknown",
      date: data["Date Submitted"] || data["Date"] || "Unknown date",
      status: data["Status"] || "Under Review",
    };
  };

  // API call to fetch data (unchanged)
useEffect(() => {
const fetchData = async () => {
  try {
    setLoading(true);
    setError(null);
    setUsingMockData(false);

    if (!selectedId) {
      throw new Error("No idea ID provided");
    }

    // Fetch all data in parallel
    const [sentinelResponse, ideaDetailsResponse, activitiesResponse] = await Promise.all([
      sentinelApiService.getSentinelData(selectedId),
      sentinelApiService.getIdeaDetails(selectedId),
      fetch(`/api/log/fetch_log?Id=${selectedId}`).then(res => res.json())
    ]);

    const transformedData = transformApiResponse(sentinelResponse);
    setSentinelData(transformedData);
    
    if (ideaDetailsResponse) {
      setIdeaDetails(ideaDetailsResponse);
    } else {
      setIdeaDetails(mockIdeaDetails);
    }

    if (activitiesResponse) {
      setRecentActivities(activitiesResponse);
    } else {
      setRecentActivities(mockRecentActivities);
    }
    
  } catch (apiError) {
    console.warn("API fetch failed, using mock data:", apiError);
    setIdeaDetails(mockIdeaDetails);
    setSentinelData(mockSentinelData);
    setRecentActivities(mockRecentActivities);
    setError(apiError.message);
    setUsingMockData(true);
  } finally {
    setLoading(false);
  }
};

  fetchData();
}, [selectedId]);

  // Retry function (unchanged)
 const handleRetry = () => {
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setUsingMockData(false);

      if (!selectedId) {
        throw new Error("No idea ID provided");
      }

      // Fetch both sentiment data and idea details
      const [sentinelResponse, ideaDetailsResponse] = await Promise.all([
        sentinelApiService.getSentinelData(selectedId),
        sentinelApiService.getIdeaDetails(selectedId)
      ]);

      const transformedData = transformApiResponse(sentinelResponse);
      setSentinelData(transformedData);
      
      // Set the idea details (which contains the title)
      if (ideaDetailsResponse) {
        setIdeaDetails(ideaDetailsResponse);
      } else {
        setIdeaDetails(mockIdeaDetails);
      }
      
      setRecentActivities(mockRecentActivities);
    } catch (apiError) {
      console.warn("API fetch failed, using mock data:", apiError);
      setIdeaDetails(mockIdeaDetails);
      setSentinelData(mockSentinelData);
      setRecentActivities(mockRecentActivities);
      setError(apiError.message);
      setUsingMockData(true);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
};

  // Get top goal alignments (unchanged)
  const getTopGoalAlignments = (goalAlignment) => {
    if (!goalAlignment || typeof goalAlignment !== "object") return [];

    return Object.entries(goalAlignment)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([label, percentage]) => ({
        label,
        percentage: Math.round(percentage * 10) / 10,
        color:
          percentage > 30 ? "var(--cares-green)" : percentage > 15 ? "var(--cares-blue)" : "var(--cares-text-label)",
      }));
  };

  // Get appropriate icon for goal alignment (unchanged)
  const getGoalIcon = (label, percentage) => {
    if (percentage > 30)
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (percentage > 15)
      return <TrendingUp className="w-4 h-4 text-blue-600" />;
    return <Target className="w-4 h-4 text-gray-600" />;
  };

  const handleBackToIdeas = () => {
    setSelectedIdeaId(null);
    setActiveTab("ideas");
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--cares-surface-board)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: 'var(--cares-surface-card)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '4px solid var(--cares-line-card)',
            borderTop: '4px solid var(--cares-blue)',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: 'var(--cares-text-3)',
            fontSize: '18px',
            fontWeight: '500'
          }}>Loading idea analysis...</p>
          <p style={{
            color: 'var(--cares-text-note)',
            fontSize: '14px',
            marginTop: '8px'
          }}>Please wait while we fetch the data</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!selectedId) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
        <div style={{ padding: '24px' }}>
          {/* Professional Header with Breadcrumbs */}
          <IdeasPageHeader
            title="Idea Sentinel"
            description="AI-powered analysis and scoring"
            breadcrumbPath="Idea Sentinel"
          >
            <button
              onClick={handleBackToIdeas}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                color: 'var(--cares-text-max)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
            >
              <ArrowLeft style={{ width: '18px', height: '18px' }} />
              <span>Back to Ideas</span>
            </button>
          </IdeasPageHeader>

          {/* Idea Selector */}
          <div style={{ marginBottom: '24px' }}>
            <IdeaSelector
              selectedId={selectedId}
              onSelectIdea={setSelectedIdeaId}
              placeholder="Select an idea to analyze..."
            />
          </div>

          {/* No Idea Selected Message */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <AlertCircle style={{
                width: '48px',
                height: '48px',
                color: 'var(--cares-blue)',
                margin: '0 auto 16px'
              }} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: 'var(--cares-text-1)',
                marginBottom: '8px'
              }}>
                No Idea Selected
              </h2>
              <p style={{
                fontSize: '14px',
                color: 'var(--cares-text-label)',
                marginBottom: '0'
              }}>
                Please select an idea from the dropdown above to view its AI-powered analysis and scoring.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const topGoalAlignments = getTopGoalAlignments(sentinelData?.goal_alignment);

  const handleOpenModal = async () => {
    try {
      setModalLoading(true);
      setShowDetailsModal(true);
      const detailedData = await sentinelApiService.getIdeaDetails(selectedId);
      if (detailedData) {
        setIdeaDetails(detailedData);
      } else {
        console.error("No idea details found");
        setIdeaDetails(mockIdeaDetails);
      }
    } catch (error) {
      console.error("Failed to fetch idea details:", error);
      // Fallback to mock data if API fails
      setIdeaDetails(mockIdeaDetails);
    } finally {
      setModalLoading(false);
    }
  };
  return (
    <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
      <div style={{ padding: '24px' }}>
        {/* Professional Header with Breadcrumbs */}
        <IdeasPageHeader
          title="Idea Sentinel"
          description={`AI-powered analysis and scoring for Idea ${selectedId}`}
          breadcrumbPath="Idea Sentinel"
        >
          <button
            onClick={handleBackToIdeas}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
              color: 'var(--cares-text-max)',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            <ArrowLeft style={{ width: '18px', height: '18px' }} />
            <span>Back to Ideas</span>
          </button>
          {usingMockData && (
            <div style={{
              background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
              border: '1px solid var(--cares-amber)',
              color: 'var(--cares-amber)',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle style={{ width: '16px', height: '16px' }} />
              <span>Demo Mode</span>
            </div>
          )}
          {error && (
            <button
              onClick={handleRetry}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'var(--cares-orange)',
                color: 'var(--cares-text-max)',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <RefreshCw style={{ width: '16px', height: '16px' }} />
              <span>Retry</span>
            </button>
          )}
        </IdeasPageHeader>

        {/* Idea Selector */}
        <div style={{ marginBottom: '24px' }}>
          <IdeaSelector
            selectedId={selectedId}
            onSelectIdea={setSelectedIdeaId}
            placeholder="Select an idea to analyze..."
          />
        </div>

        {/* Scoring Cards - 3 columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '24px',
          marginBottom: '32px'
        }}>
              {/* Alignment Score */}
              <div style={{
                background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '100px',
                  height: '100px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%'
                }}></div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Alignment Score
                    </span>
                    <CheckCircle style={{ width: '20px', height: '20px', color: 'rgba(255, 255, 255, 0.8)' }} />
                  </div>
                  <div style={{ fontSize: '48px', fontWeight: '700', color: 'var(--cares-text-max)', marginBottom: '8px' }}>
                    {(sentinelData?.alignment_score || 0).toFixed(1)}
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${((sentinelData?.alignment_score || 0) / 10) * 100}%`,
                      height: '100%',
                      background: 'var(--cares-surface-card)',
                      borderRadius: '3px',
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                  <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '12px', marginTop: '12px', margin: 0 }}>
                    Strategic alignment with organizational goals
                  </p>
                </div>
              </div>

              {/* Urgency Score */}
              <div style={{
                background: 'linear-gradient(135deg, var(--cares-amber) 0%, var(--cares-amber) 100%)',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 8px 24px rgba(245, 158, 11, 0.25)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '100px',
                  height: '100px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%'
                }}></div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Urgency Score
                    </span>
                    <AlertCircle style={{ width: '20px', height: '20px', color: 'rgba(255, 255, 255, 0.8)' }} />
                  </div>
                  <div style={{ fontSize: '48px', fontWeight: '700', color: 'var(--cares-text-max)', marginBottom: '8px' }}>
                    {(sentinelData?.urgency_score || 0).toFixed(1)}
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${((sentinelData?.urgency_score || 0) / 10) * 100}%`,
                      height: '100%',
                      background: 'var(--cares-surface-card)',
                      borderRadius: '3px',
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                  <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '12px', marginTop: '12px', margin: 0 }}>
                    Time sensitivity and implementation priority
                  </p>
                </div>
              </div>

              {/* Value Score */}
              <div style={{
                background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.25)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '100px',
                  height: '100px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%'
                }}></div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Value Score
                    </span>
                    <TrendingUp style={{ width: '20px', height: '20px', color: 'rgba(255, 255, 255, 0.8)' }} />
                  </div>
                  <div style={{ fontSize: '48px', fontWeight: '700', color: 'var(--cares-text-max)', marginBottom: '8px' }}>
                    {(sentinelData?.value_score || 0).toFixed(1)}
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${((sentinelData?.value_score || 0) / 10) * 100}%`,
                      height: '100%',
                      background: 'var(--cares-surface-card)',
                      borderRadius: '3px',
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                  <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '12px', marginTop: '12px', margin: 0 }}>
                    Expected business value and ROI potential
                  </p>
                </div>
              </div>
            </div>

            {/* Main Content Grid - 50/50 width */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '24px' }}>
              {/* Goal Alignment */}
              <div style={{
                background: 'var(--cares-surface-card)',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid var(--cares-line-card)'
              }}>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Target style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>
                      Goal Alignment
                    </h2>
                  </div>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <Info
                      style={{
                        width: '18px',
                        height: '18px',
                        color: 'var(--cares-text-note)',
                        cursor: 'pointer',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--cares-text-label)';
                        e.currentTarget.nextElementSibling.style.opacity = '1';
                        e.currentTarget.nextElementSibling.style.visibility = 'visible';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--cares-text-note)';
                        e.currentTarget.nextElementSibling.style.opacity = '0';
                        e.currentTarget.nextElementSibling.style.visibility = 'hidden';
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      right: '0',
                      top: '28px',
                      background: 'var(--cares-surface-board)',
                      color: 'var(--cares-text-max)',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      width: '280px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                      zIndex: 10,
                      opacity: 0,
                      visibility: 'hidden',
                      transition: 'opacity 0.2s, visibility 0.2s',
                      lineHeight: '1.5'
                    }}>
                      Scores computed based on goals defined in the Goals Alignment section and evaluated through the Rules Engine.
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {topGoalAlignments.length > 0 ? (
                    topGoalAlignments.map((goal, index) => (
                      <GoalAlignmentItem
                        key={index}
                        icon={getGoalIcon(goal.label, goal.percentage)}
                        label={goal.label}
                        percentage={goal.percentage}
                        color={goal.color}
                      />
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Target style={{ width: '48px', height: '48px', color: 'var(--cares-text-dim)', margin: '0 auto 16px' }} />
                      <p style={{ color: 'var(--cares-text-note)', fontSize: '14px', margin: 0 }}>No goal alignment data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* AI Summary */}
              <div style={{
                background: 'var(--cares-surface-card)',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid var(--cares-line-card)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Lightbulb style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>
                    AI Summary
                  </h2>
                </div>
                <p style={{
                  color: 'var(--cares-text-3)',
                  lineHeight: '1.7',
                  whiteSpace: 'pre-line',
                  fontSize: '14px',
                  margin: 0
                }}>
                  {sentinelData?.ai_summary || "No summary available"}
                </p>
              </div>
            </div>

            {/* Idea Details - Full Width */}
            <div style={{
              background: 'var(--cares-surface-card)',
              borderRadius: '16px',
              padding: '28px',
              marginBottom: '24px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid var(--cares-line-card)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileText style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>
                    Idea Details
                  </h2>
                </div>
                <button
                  onClick={handleOpenModal}
                  style={{
                    background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                    color: 'var(--cares-text-max)',
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                  }}
                >
                  View Full Details
                </button>
              </div>

              <div style={{
                background: 'var(--cares-surface-board)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid var(--cares-line-card)'
              }}>
                <p style={{
                  color: 'var(--cares-text-3)',
                  lineHeight: '1.7',
                  whiteSpace: 'pre-line',
                  fontSize: '14px',
                  margin: 0
                }}>
                  {ideaDetails?.description || "No description available"}
                </p>
              </div>
            </div>

            {/* Tags and Categories - 50/50 width */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '24px' }}>
              {/* Tags */}
              <div style={{
                background: 'var(--cares-surface-card)',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid var(--cares-line-card)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--cares-teal) 0%, var(--cares-teal) 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Lightbulb style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                  </div>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>
                      Keyword Analysis
                    </h2>
                    <p style={{ fontSize: '12px', color: 'var(--cares-text-note)', margin: 0 }}>Auto-Generated Tags</p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {sentinelData?.auto_tags?.length > 0 ? (
                    sentinelData.auto_tags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '8px 16px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '600',
                          background: 'linear-gradient(135deg, color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card)) 0%, color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card)) 100%)',
                          color: 'var(--cares-blue)',
                          border: '1px solid color-mix(in srgb, var(--cares-blue) 30%, var(--cares-surface-card))',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0', width: '100%' }}>
                      <Lightbulb style={{ width: '48px', height: '48px', color: 'var(--cares-text-dim)', margin: '0 auto 16px' }} />
                      <p style={{ color: 'var(--cares-text-note)', fontSize: '14px', margin: 0 }}>No tags available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div style={{
                background: 'var(--cares-surface-card)',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid var(--cares-line-card)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Eye style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>
                    Suggested Categories
                  </h2>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {sentinelData?.suggested_categories?.length > 0 ? (
                    sentinelData.suggested_categories.map((category, index) => (
                      <CategoryMatchItem
                        key={index}
                        label={category.category}
                        percentage={category.match_percentage}
                        color="var(--cares-green)"
                      />
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Eye style={{ width: '48px', height: '48px', color: 'var(--cares-text-dim)', margin: '0 auto 16px' }} />
                      <p style={{ color: 'var(--cares-text-note)', fontSize: '14px', margin: 0 }}>No categories available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Activity - Full width */}
            <div style={{
              background: 'var(--cares-surface-card)',
              borderRadius: '16px',
              padding: '28px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid var(--cares-line-card)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--cares-amber) 0%, var(--cares-amber) 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Clock style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>
                    Recent Activity
                  </h2>
                </div>
                {recentActivities.length > 2 && (
                  <button
                    onClick={() => setShowActivitiesModal(true)}
                    style={{
                      color: 'var(--cares-blue)',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cares-blue)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--cares-blue)'}
                  >
                    View All History →
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {recentActivities.length > 0 ? (
                  recentActivities.slice(0, 2).map((activity, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        padding: '16px',
                        background: 'var(--cares-surface-board)',
                        borderRadius: '12px',
                        border: '1px solid var(--cares-line-card)',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--cares-surface-board)';
                        e.currentTarget.style.borderColor = 'var(--cares-line-strong)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--cares-surface-board)';
                        e.currentTarget.style.borderColor = 'var(--cares-line-card)';
                      }}
                    >
                      <div style={{
                        width: '44px',
                        height: '44px',
                        background: (activity.Role && typeof activity.Role === 'string' && activity.Role.includes('AI'))
                          ? 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)'
                          : 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {(activity.Role && typeof activity.Role === 'string' && activity.Role.includes('AI')) ? (
                          <Lightbulb style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                        ) : (
                          <User style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)', margin: '0 0 6px 0' }}>
                          {activity.Events}
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--cares-text-label)', margin: 0 }}>
                          {activity.Role} • {activity.Date}
                        </p>
                        {activity.Comments && (
                          <p style={{ fontSize: '12px', color: 'var(--cares-text-3)', marginTop: '8px', margin: '8px 0 0 0' }}>
                            {activity.Comments}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Clock style={{ width: '48px', height: '48px', color: 'var(--cares-text-dim)', margin: '0 auto 16px' }} />
                    <p style={{ color: 'var(--cares-text-note)', fontSize: '14px', margin: 0 }}>No recent activity available</p>
                  </div>
                )}
              </div>
            </div>
      </div>

      {/* Idea Details Modal - Fixed to properly show/hide */}
      {showDetailsModal && (
        <IdeaDetailsModal
          ideaDetails={ideaDetails}
          onClose={() => setShowDetailsModal(false)}
          isLoading={modalLoading}
        />
      )}
      {showActivitiesModal && (
        <ActivitiesModal
          activities={recentActivities}
          onClose={() => setShowActivitiesModal(false)}
        />
      )}
    </div>
  );
};

export default IdeaSentinel;
